import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../../theme/themes';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import { useAuth } from '../../contexts/AuthContext';

interface CredentialSummary {
  username: string;
  dateCreated: string;
  rotatedAt: string;
  lastSyncedAt: string | null;
  synced: boolean;
}

interface DeviceCredentialRow {
  deviceId: string;
  deviceName: string;
  deviceTypeId: string;
  deviceTypeName: string | null;
  credential: CredentialSummary | null;
}

interface RevealedPassword {
  username: string;
  password: string;
  synced: boolean;
  message: string;
}

const ALL_TYPES = 'all';

const FtpCredentialManagement: React.FC = () => {
  const auth = useAuth();
  const { mode } = useThemeMode();
  const accessToken = auth.tokens?.accessToken;
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const [rows, setRows] = useState<DeviceCredentialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const [passwordMode, setPasswordMode] = useState<'auto' | 'custom'>('auto');
  const [customPassword, setCustomPassword] = useState('');

  const [actionLoading, setActionLoading] = useState(false);
  const [revealed, setRevealed] = useState<RevealedPassword | null>(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/ftp-credentials', { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load FTP credential status');
      const data = await res.json();
      setRows(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load FTP credential status');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (!accessToken) return;
    loadRows();
  }, [accessToken, loadRows]);

  const deviceTypes = useMemo(
    () =>
      Array.from(
        new Map(
          rows
            .filter((r) => r.deviceTypeName)
            .map((r) => [r.deviceTypeId, r.deviceTypeName as string])
        ).entries()
      ).sort((a, b) => a[1].localeCompare(b[1])),
    [rows]
  );

  const filteredRows = useMemo(
    () => (typeFilter === ALL_TYPES ? rows : rows.filter((r) => r.deviceTypeId === typeFilter)),
    [rows, typeFilter]
  );

  useEffect(() => {
    if (selectedDeviceId && !filteredRows.some((r) => r.deviceId === selectedDeviceId)) {
      setSelectedDeviceId('');
    }
  }, [filteredRows, selectedDeviceId]);

  const selectedRow = rows.find((r) => r.deviceId === selectedDeviceId) || null;

  const handleTypeFilterChange = (event: SelectChangeEvent<string>) => {
    setTypeFilter(event.target.value);
  };

  const handleDeviceChange = (event: SelectChangeEvent<string>) => {
    setSelectedDeviceId(event.target.value);
    setPasswordMode('auto');
    setCustomPassword('');
  };

  const handleRowClick = (params: GridRowParams) => {
    setSelectedDeviceId(params.row.deviceId);
    setPasswordMode('auto');
    setCustomPassword('');
  };

  const submitCredential = async () => {
    if (!selectedRow) return;
    setActionLoading(true);
    setError(null);
    try {
      const path = selectedRow.credential
        ? `/api/devices/${selectedRow.deviceId}/ftp-credential/rotate`
        : `/api/devices/${selectedRow.deviceId}/ftp-credential`;
      const body =
        passwordMode === 'custom' && customPassword ? { password: customPassword } : undefined;
      const res = await fetch(path, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save credential');
      setRevealed(data);
      setCustomPassword('');
      setPasswordMode('auto');
      await loadRows();
    } catch (err: any) {
      setError(err.message || 'Failed to save credential');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveClick = () => {
    if (selectedRow?.credential) {
      setRotateConfirmOpen(true);
    } else {
      submitCredential();
    }
  };

  const copyPassword = () => {
    if (!revealed) return;
    navigator.clipboard.writeText(revealed.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeRevealDialog = () => {
    setRevealed(null);
    setCopied(false);
  };

  const customPasswordInvalid =
    passwordMode === 'custom' && customPassword.length > 0 && customPassword.length < 8;

  const columns: GridColDef[] = [
    { field: 'deviceName', headerName: 'Device Name', flex: 1, minWidth: 180 },
    {
      field: 'deviceTypeName',
      headerName: 'Type',
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        params.row.deviceTypeName ? (
          <Chip label={params.row.deviceTypeName} size="small" color="primary" />
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'username',
      headerName: 'FTP Username',
      width: 220,
      valueGetter: (params) => params.row.credential?.username || '',
      renderCell: (params: GridRenderCellParams) =>
        params.row.credential ? (
          <Typography variant="body2" fontFamily="monospace">
            {params.row.credential.username}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">No credential</Typography>
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const credential = params.row.credential as CredentialSummary | null;
        if (!credential) return <Chip label="Not provisioned" size="small" />;
        return (
          <Chip
            label={credential.synced ? 'Synced' : 'Not synced'}
            color={credential.synced ? 'success' : 'warning'}
            size="small"
          />
        );
      },
    },
    {
      field: 'rotatedAt',
      headerName: 'Last Rotated',
      width: 170,
      valueGetter: (params) =>
        params.row.credential?.rotatedAt ? new Date(params.row.credential.rotatedAt) : null,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="caption">
          {params.row.credential?.rotatedAt
            ? new Date(params.row.credential.rotatedAt).toLocaleString()
            : '—'}
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <KeyIcon sx={{ mr: 1 }} />
        <Typography variant="h5">FTP Credential Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="ftp-type-filter-label">Device Type</InputLabel>
            <Select
              labelId="ftp-type-filter-label"
              value={typeFilter}
              label="Device Type"
              onChange={handleTypeFilterChange}
            >
              <MenuItem value={ALL_TYPES}>
                <em>All Types</em>
              </MenuItem>
              {deviceTypes.map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel id="ftp-device-select-label">Device</InputLabel>
            <Select
              labelId="ftp-device-select-label"
              value={selectedDeviceId}
              label="Device"
              onChange={handleDeviceChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select a device…</em>
              </MenuItem>
              {filteredRows.map((row) => (
                <MenuItem key={row.deviceId} value={row.deviceId}>
                  {row.deviceName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {!selectedRow ? (
          <Typography color="text.secondary">
            Select a device above to provision or rotate its FTP credential.
          </Typography>
        ) : (
          <Stack spacing={2} alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">{selectedRow.deviceName}</Typography>
              {selectedRow.credential ? (
                <Chip
                  label={selectedRow.credential.synced ? 'Synced' : 'Not synced'}
                  color={selectedRow.credential.synced ? 'success' : 'warning'}
                  size="small"
                />
              ) : (
                <Chip label="Not provisioned" size="small" />
              )}
            </Stack>

            {selectedRow.credential && (
              <Typography variant="body2" color="text.secondary">
                Current username: <code>{selectedRow.credential.username}</code>
              </Typography>
            )}

            <ToggleButtonGroup
              value={passwordMode}
              exclusive
              size="small"
              onChange={(_e, value) => {
                if (value) setPasswordMode(value);
              }}
            >
              <ToggleButton value="auto">Auto-generate password</ToggleButton>
              <ToggleButton value="custom">Custom password</ToggleButton>
            </ToggleButtonGroup>

            {passwordMode === 'custom' && (
              <TextField
                label="Custom Password"
                value={customPassword}
                onChange={(e) => setCustomPassword(e.target.value)}
                error={customPasswordInvalid}
                helperText={
                  customPasswordInvalid
                    ? 'Must be at least 8 characters'
                    : 'Minimum 8 characters. Leave the toggle on Auto-generate to have one created for you.'
                }
                sx={{ minWidth: 320 }}
              />
            )}

            <Button
              variant="contained"
              disabled={
                actionLoading ||
                (passwordMode === 'custom' && (customPassword.length === 0 || customPasswordInvalid))
              }
              onClick={handleSaveClick}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {selectedRow.credential ? 'Rotate & Save' : 'Create & Save'}
            </Button>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ height: 420, width: '100%', mb: 3 }}>
        <DataGrid
          rows={filteredRows}
          getRowId={(row) => row.deviceId}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'deviceName', sort: 'asc' }] },
          }}
          onRowClick={handleRowClick}
          rowSelectionModel={selectedDeviceId ? [selectedDeviceId] : []}
          sx={{
            ...dataGridHeaderSx(mode),
            '& .MuiDataGrid-row': { cursor: 'pointer' },
          }}
        />
      </Paper>

      {/* One-time password reveal dialog */}
      <Dialog open={!!revealed} onClose={closeRevealDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Save this password now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This password is shown once and cannot be retrieved again. Copy it into the camera's FTP
            configuration now — if it's lost, you'll need to rotate again to get a new one.
          </Alert>
          <Stack spacing={2}>
            <TextField label="Username" value={revealed?.username || ''} InputProps={{ readOnly: true }} fullWidth />
            <TextField
              label="Password"
              value={revealed?.password || ''}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={copyPassword} edge="end" size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              fullWidth
            />
            {copied && <Alert severity="success">Copied to clipboard.</Alert>}
            {revealed && !revealed.synced && <Alert severity="warning">{revealed.message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevealDialog} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate confirmation */}
      <Dialog open={rotateConfirmOpen} onClose={() => setRotateConfirmOpen(false)}>
        <DialogTitle>Rotate FTP Password</DialogTitle>
        <DialogContent>
          <Typography>
            This device already has an active credential. Saving generates a new password and
            invalidates the old one once synced — the camera's FTP configuration will need updating.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setRotateConfirmOpen(false);
              submitCredential();
            }}
            color="warning"
            variant="contained"
          >
            Rotate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FtpCredentialManagement;
